/**
 * frontend/src/sdk/hooks/use-hindcast.ts
 *
 * Typed TanStack Query hooks for the nightly hindcast harness
 * (real-market model scoring: Brier / hit-rate / edge-capture).
 *
 * Backend: GET /api/hindcast/latest  — most recent run summary
 *          GET /api/hindcast/history — run history, most recent first
 * Runs are written by HindcastRunner.run_nightly (manual POST /run trigger);
 * an empty payload means no runs have been recorded yet — render that
 * honestly, never as zero scores.
 */

import { useQuery } from "@tanstack/react-query";

import { apiGet } from "@/lib/api-client";

export interface HindcastRun {
  id: number;
  run_date: string;
  lookback_days: number;
  markets_tested: number;
  /** null on legacy rows that pre-date the counters. */
  markets_degraded?: number | null;
  markets_failed?: number | null;
  avg_brier: number | null;
  win_rate: number | null;
  edge_capture: number | null;
  model_count: number | null;
  duration_ms: number | null;
  created_at: string | null;
}

export interface HindcastLatestResponse {
  run: (HindcastRun & { results?: unknown[]; results_schema_version?: number }) | null;
  message?: string;
}

export interface HindcastHistoryResponse {
  runs: HindcastRun[];
  count: number;
}

export function useHindcastLatest() {
  return useQuery({
    queryKey: ["hindcast", "latest"],
    queryFn: () => apiGet<HindcastLatestResponse>("/api/hindcast/latest"),
    staleTime: 300_000,
    refetchInterval: 600_000,
  });
}

export function useHindcastHistory(limit = 30) {
  return useQuery({
    queryKey: ["hindcast", "history", limit],
    queryFn: () =>
      apiGet<HindcastHistoryResponse>(`/api/hindcast/history?limit=${limit}`),
    staleTime: 300_000,
    refetchInterval: 600_000,
  });
}
